import useSWR, { type SWRConfiguration } from 'swr';
import type { DisasterRecoverySettingsResponse } from '@mls-wallet/shared-types';
import denApi from '@/lib/api/denApi';
import { useMyOrganization } from '@/hooks/organization/use-my-organization';

const DISASTER_RECOVERY_PROPOSALS_LIST_URL =
  'admin/organization/disaster-recovery-proposals';

export type ServerDisasterRecoveryProposalsResponse =
  DisasterRecoverySettingsResponse;

const fetchDisasterRecoveryProposalList =
  async (): Promise<ServerDisasterRecoveryProposalsResponse> =>
    await denApi.get<ServerDisasterRecoveryProposalsResponse, void>(
      DISASTER_RECOVERY_PROPOSALS_LIST_URL
    );

/**
 * Shared list hook for DR proposal consumers. Centralizes the list API call so
 * feature hooks can focus on selecting/deriving their own view state.
 */
export function useDisasterRecoveryProposalList(
  swrOptions?: SWRConfiguration<ServerDisasterRecoveryProposalsResponse>
) {
  const { organization } = useMyOrganization();
  const isAdmin = Boolean(organization?.isAdmin);
  const key = isAdmin ? DISASTER_RECOVERY_PROPOSALS_LIST_URL : null;

  const { data, error, isLoading, mutate } =
    useSWR<ServerDisasterRecoveryProposalsResponse>(
      key,
      key ? fetchDisasterRecoveryProposalList : null,
      {
        revalidateOnFocus: false,
        ...(swrOptions ?? {}),
      }
    );

  return {
    data: data ?? null,
    error,
    isLoading: Boolean(key) && isLoading,
    refresh: mutate,
  };
}
